import { useMemo, useState } from 'react';

const PRESET_POINTS = [10, 15, 20, 30];

function toNumber(value: string) {
  const parsed = Number(value.replace(',', '.'));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function formatMoney(value: number) {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

type NumberFieldProps = {
  id: string;
  label: string;
  hint?: string;
  value: string;
  step?: string;
  onChange: (value: string) => void;
};

function NumberField({ id, label, hint, value, step = '1', onChange }: NumberFieldProps) {
  return (
    <div className="min-w-0">
      <label htmlFor={id} className="block text-xs font-semibold text-foreground-800">
        {label}
      </label>
      {hint && <p className="mt-0.5 text-[11px] leading-snug text-foreground-500">{hint}</p>}
      <input
        id={id}
        type="number"
        inputMode="decimal"
        min="0"
        step={step}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="mt-1.5 box-border w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 font-num text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
      />
    </div>
  );
}

export default function SpreadCostCalculator() {
  const [points, setPoints] = useState('15');
  const [lots, setLots] = useState('0.10');
  const [pipValue, setPipValue] = useState('10');

  const result = useMemo(() => {
    const pips = toNumber(points) / 10;
    const perLot = pips * toNumber(pipValue);
    return { pips, perLot, total: perLot * toNumber(lots) };
  }, [points, lots, pipValue]);

  return (
    <div className="mt-1 rounded-xl border border-background-200 bg-background-100/60 p-4 md:p-5">
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-500 text-background-50">
          <i className="ri-calculator-line text-lg" />
        </span>
        <div className="min-w-0">
          <p className="font-heading text-sm font-semibold text-foreground-950 md:text-base">
            เครื่องคิดต้นทุนจาก Spread
          </p>
          <p className="mt-1 text-xs leading-relaxed text-foreground-600">
            ใส่ค่าที่เพิ่งเห็นใน Market Watch ลงไป แล้วดูว่าออเดอร์นั้นถูกหักต้นทุนไปเท่าไร
          </p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <NumberField
          id="spread-points"
          label="Spread (Point)"
          hint="ค่าที่เห็นในคอลัมน์ Spread"
          value={points}
          onChange={setPoints}
        />
        <NumberField
          id="spread-lots"
          label="ขนาดล็อต"
          hint="0.01 = 1 micro lot"
          value={lots}
          step="0.01"
          onChange={setLots}
        />
        <NumberField
          id="spread-pip-value"
          label="Pip Value ต่อ 1 ล็อต (USD)"
          hint="คู่เงินที่ quote เป็น USD ใช้ 10"
          value={pipValue}
          onChange={setPipValue}
        />
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-medium text-foreground-500">ค่าที่พบบ่อย:</span>
        {PRESET_POINTS.map((preset) => {
          const active = points === String(preset);
          return (
            <button
              key={preset}
              type="button"
              onClick={() => setPoints(String(preset))}
              className={`cursor-pointer whitespace-nowrap rounded-full px-2.5 py-1 font-num text-xs font-medium transition-colors ${
                active
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-secondary-100 text-secondary-900 hover:bg-secondary-200'
              }`}
            >
              {preset}
            </button>
          );
        })}
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="rounded-lg border border-background-200 bg-background-50 p-3.5">
          <p className="text-[11px] font-semibold tracking-wide text-foreground-500">ต้นทุนต่อ 1.00 ล็อต</p>
          <p className="mt-1 flex items-baseline gap-1.5 font-num text-xl font-semibold text-primary-700">
            ${formatMoney(result.perLot)}
            <span className="text-xs font-medium text-foreground-500">USD</span>
          </p>
          <p className="mt-1 font-num text-[11px] text-foreground-500">
            {result.pips.toFixed(1)} Pip × {formatMoney(toNumber(pipValue))} USD
          </p>
        </div>

        <div className="rounded-lg border border-accent-300 bg-accent-50 p-3.5">
          <p className="text-[11px] font-semibold tracking-wide text-accent-700">ต้นทุนของออเดอร์นี้</p>
          <p className="mt-1 flex items-baseline gap-1.5 font-num text-xl font-semibold text-accent-700">
            ${formatMoney(result.total)}
            <span className="text-xs font-medium text-foreground-500">USD</span>
          </p>
          <p className="mt-1 font-num text-[11px] text-foreground-500">
            {formatMoney(toNumber(lots))} ล็อต × ${formatMoney(result.perLot)}
          </p>
        </div>
      </div>

      <p className="mt-3 text-[11px] leading-relaxed text-foreground-500">
        คิดจากสูตร (Spread ÷ 10) × Pip Value × จำนวนล็อต โดย 10 Point เท่ากับ 1 Pip ค่า Pip Value เริ่มต้นที่ 10 USD
        ซึ่งใช้กับคู่เงินที่ราคา quote เป็น USD เช่น EUR/USD ถ้าเทรด USD/JPY, XAUUSD หรือคู่เงินที่ quote เป็นสกุลอื่น
        ให้แก้ช่อง Pip Value ตามสเปกของคู่นั้นในหน้าต่าง Specification
      </p>
    </div>
  );
}